import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Alert, Button, Card, TextInput } from '@opendate-app/setlist';
import '../styles/boxOfficeGiftCardPayment.css';

const axios = require('axios').default;
var _ = require('lodash');

const UNAVAILABLE_MESSAGE = "Gift cards can't be applied right now. Please try again.";
const REMOVE_FAILED_MESSAGE = "We couldn't remove that gift card. Please try again.";
const GIFT_CARD_FIELDS = ["applied_gift_card_summaries", "gift_card_adjusted_total"];

const BoxOfficeGiftCardPayment = ({
  team,
  csrfToken,
  ticketReservation,
  ticketReservationChanged
}) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const formatCents = (cents) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: team.base_currency_symbol || 'USD'
  }).format(cents / 100);

  const redemptionsPath = `/teams/${team.id}/manual_ticket_reservations/${ticketReservation.token}/gift_card_redemptions`;
  const appliedCards = ticketReservation.applied_gift_card_summaries || [];
  const leftToCollectCents = Math.round(parseFloat(ticketReservation.gift_card_adjusted_total || 0) * 100);
  const fullyCovered = appliedCards.length > 0 && leftToCollectCents === 0;

  const reservationUpdated = (data) => {
    ticketReservationChanged({ ...ticketReservation, ..._.pick(data, GIFT_CARD_FIELDS) });
  };

  const applyCode = () => {
    if (code.trim().length === 0 || isApplying) {
      return;
    }

    setError(null);
    setNotice(null);
    setIsApplying(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.post(redemptionsPath, { gift_card_code: code.trim() })
      .then(({ data }) => {
        setCode("");
        reservationUpdated(data);
      })
      .catch((requestError) => {
        const body = requestError && requestError.response && requestError.response.data;
        setError((body && body.errors && body.errors[0]) || UNAVAILABLE_MESSAGE);
      })
      .finally(() => setIsApplying(false));
  };

  const removeCard = (redemptionId) => {
    if (removingId) {
      return;
    }

    setError(null);
    setNotice(null);
    setRemovingId(redemptionId);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.delete(`${redemptionsPath}/${redemptionId}`)
      .then(({ data }) => {
        const releasedCodes = data.released_gift_card_code_last4s || [];

        if (releasedCodes.length > 0) {
          setNotice(`No longer needed for this order, so removed: ${releasedCodes.map((last4) => `gift card •••• ${last4}`).join(", ")}.`);
        }
        reservationUpdated(data);
      })
      .catch(() => setError(REMOVE_FAILED_MESSAGE))
      .finally(() => setRemovingId(null));
  };

  return (
    <div className="mt-4 box-office-gift-card-payment">
      <Card header={<strong>Gift card</strong>}>
        {appliedCards.map((card) => (
          <div key={card.id} className="applied-gift-card">
            <div className="applied-gift-card-header">
              <strong>Gift card •••• {card.code_last4}</strong>
              <Button variant="link-gray"
                      size="sm"
                      disabled={removingId !== null}
                      onClick={() => removeCard(card.id)}>
                Remove
              </Button>
            </div>
            <div className="applied-gift-card-row">
              <span>Balance</span>
              <span>{formatCents(card.amount_cents + card.balance_after_cents)}</span>
            </div>
            <div className="applied-gift-card-row">
              <span>Applied to this order</span>
              <span>{formatCents(card.amount_cents)}</span>
            </div>
            <div className="applied-gift-card-row">
              <span>Left on card after</span>
              <span>{formatCents(card.balance_after_cents)}</span>
            </div>
          </div>
        ))}
        {fullyCovered ? (
          <Alert color="success"
                 size="full-width"
                 title="The gift card covers the full order."
                 description="Nothing is left to collect." />
        ) : (
          <>
            <div className="gift-card-code-entry">
              <div className="gift-card-code-input">
                <TextInput size="sm"
                           aria-label="Gift card code"
                           placeholder="Type or scan a gift card code"
                           autoComplete="off"
                           value={code}
                           error={error || undefined}
                           onChange={(event) => setCode(event.target.value)}
                           onKeyDown={(event) => {
                             if (event.key === "Enter") {
                               event.preventDefault();
                               applyCode();
                             }
                           }} />
              </div>
              <Button variant="secondary"
                      size="sm"
                      disabled={code.trim().length === 0 || isApplying}
                      onClick={applyCode}>
                {isApplying ? "Checking…" : "Apply"}
              </Button>
            </div>
            {appliedCards.length > 0 ? (
              <p className="gift-card-message">
                Collect the remaining {formatCents(leftToCollectCents)} in cash.
              </p>
            ) : null}
          </>
        )}
        {fullyCovered && error ? (
          <p className="gift-card-message gift-card-message-error">{error}</p>
        ) : null}
        {notice ? (
          <p className="gift-card-message gift-card-message-muted gift-card-notice">{notice}</p>
        ) : null}
      </Card>
    </div>
  );
};

BoxOfficeGiftCardPayment.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  ticketReservation: PropTypes.object.isRequired,
  ticketReservationChanged: PropTypes.func.isRequired
};

export default BoxOfficeGiftCardPayment;
